import React, { useContext, useEffect, useState } from 'react';
import styles from './listaNCMItem.module.scss';
import { BrButton } from '@govbr-ds/react-components';
import { useBackEndRequests } from '../../../../../../../lib/backEndRequests';
import { useNotificacao } from '../../../../../../../../../hooks/useNotificacao';
import { Button, Modal } from 'react-dsgov';
import { DiefCfemContext } from '../../../../../../../../../contexts/DiefCfemContext';
import { IReceitaBrutaNCMReceber } from '../../../../../../../../../interfaces/IReceitaBruta';
import ItemReceitaBruta from './components/itemReceitaBruta/itemReceitaBruta';
import { ETipoOperacao } from '../../../../../../../../../enums/ETipoOperacao';
import { IVendaNacionalNCMReceber } from '../../../../../../../../../interfaces/IVendaNacional';
import ItemVendaNacional from './components/itemVendaNacional/itemVendaNacional';
import { INCMReceitaBrutaCalculada } from '../../../../../../../../../interfaces/IReceitaBrutaCalculada';
import {
  EDiscriminadorMetodoReceitaBrutaCalculada,
  EMetodoReceitaBrutaCalculada,
} from '../../../../../../../../../enums/EMetodoBaseCalculo';
import {
  IReceitaExportacoesNCM,
  IReceitaExportacoesVinculadaNCM,
} from '../../../../../../../../../interfaces/IReceitaExportacoes';
import ItemReceitaBrutaCalculada from './components/itemReceitaBrutaCalculada/itemReceitaBrutaCalculada';
import ItemReceitaCalculadaExportacoes from './components/itemReceitaExportacoes/itemReceitaExportacoes';
import CustomConfirmer from '../../../../../../../../../components/customConfirmer/customConfirmer';
import { formatNumber } from '../../../../../../../../../utils/formatNumber';

interface IListaNCMItemProps {
  ncm:
    | IReceitaBrutaNCMReceber
    | IVendaNacionalNCMReceber
    | INCMReceitaBrutaCalculada
    | (IReceitaExportacoesNCM | IReceitaExportacoesVinculadaNCM);
  isReadOnly?: boolean;
  refetchListaNCM: () => void;
  setIsEdicaoNcm?: React.Dispatch<React.SetStateAction<boolean>>;
  refetchDadosAnteriores?: () => void;
  type: ETipoOperacao;
}

const ListaNCMItem = ({ ncm, isReadOnly = false, refetchListaNCM, setIsEdicaoNcm, type }: IListaNCMItemProps) => {
  const [isAccordionOpen, setIsAccordionOpen] = useState(false);
  const [isModalOpened, setIsModalOpened] = useState(false);
  const [codigoNCM, setCodigoNCM] = useState<string | undefined>(undefined);

  const {
    deleteNCMReceitaBruta,
    deleteNCMVendaNacional,
    deleteNCMReceitaBrutaCalculadaPrecoCorrente,
    deleteNCMReceitaBrutaCalculadaValorReferencia,
    deleteNCMReceitaExportacoes,
    deleteNCMVinculadaReceitaExportacoes,
  } = useBackEndRequests();

  const mostrarNotificacao = useNotificacao();
  const context = useContext(DiefCfemContext);

  const deleteNCM = async () => {
    if (type === ETipoOperacao.RECEITA_BRUTA_TRIBUTOS_INCIDENTES) {
      await deleteNCMReceitaBruta(ncm?.id ?? -1)
        .then(() => {
          refetchListaNCM();
        })
        .catch((err) => {
          mostrarNotificacao({
            tipo: 'danger',
            conteudo: 'Erro ao cancelar inclusão de NCM. Por favor, tente novamente.',
            ...(err && { complementoSentryId: err }),
          });
        });
    } else if (type === ETipoOperacao.RECEITA_BRUTA_CALCULADA) {
      let deleteNCMReceitaBrutaCalculada = deleteNCMReceitaBrutaCalculadaPrecoCorrente;

      if (EDiscriminadorMetodoReceitaBrutaCalculada.VALOR_REFERENCIA in ncm) {
        deleteNCMReceitaBrutaCalculada = deleteNCMReceitaBrutaCalculadaValorReferencia;
      }

      await deleteNCMReceitaBrutaCalculada(ncm?.id ?? -1)
        .then(() => {
          refetchListaNCM();
        })
        .catch(() => {
          mostrarNotificacao({
            tipo: 'danger',
            conteudo: 'Erro ao cancelar inclusão de NCM. Por favor, tente novamente.',
          });
        })
        .finally(() => {
          setIsModalOpened(false);
        });
    } else if (type === ETipoOperacao.VENDA_TERRITORIO_NACIONAL_EMPRESAS_COLIGADAS_OU_MESMO_GRUPO_ECONOMICO) {
      await deleteNCMVendaNacional(ncm?.id ?? -1)
        .then(() => {
          refetchListaNCM();
        })
        .catch((err) => {
          mostrarNotificacao({
            tipo: 'danger',
            conteudo: 'Erro ao cancelar inclusão de NCM. Por favor, tente novamente.',
            ...(err && { complementoSentryId: err }),
          });
        });
    } else if (type === ETipoOperacao.RECEITA_CALCULADA_EXPORTACOES) {
      const ncmExportacoes = ncm as IReceitaExportacoesNCM | IReceitaExportacoesVinculadaNCM;
      let deleteNCMReceitaExportacoesEscolhido = deleteNCMReceitaExportacoes;

      if (ncmExportacoes?.hasExportacao) {
        deleteNCMReceitaExportacoesEscolhido = deleteNCMVinculadaReceitaExportacoes;
      }

      await deleteNCMReceitaExportacoesEscolhido(ncmExportacoes?.id ?? -1)
        .then(() => {
          refetchListaNCM();
        })
        .catch((err) => {
          mostrarNotificacao({
            tipo: 'danger',
            conteudo: 'Erro ao cancelar inclusão de NCM. Por favor, tente novamente.',
            ...(err && { complementoSentryId: err }),
          });
        })
        .finally(() => {
          setIsModalOpened(false);
        });
    }
  };

  const editarNCM = () => {
    if (type === ETipoOperacao.RECEITA_BRUTA_TRIBUTOS_INCIDENTES) {
      context?.setIdReceitaBrutaNCM(ncm?.id ?? -1);
    } else if (type === ETipoOperacao.RECEITA_BRUTA_CALCULADA) {
      const receitaBrutaCalculada = ncm as IReceitaBrutaNCMReceber;
      let tipoMetodo = EMetodoReceitaBrutaCalculada.PRECO_CORRENTE;

      if (EDiscriminadorMetodoReceitaBrutaCalculada.VALOR_REFERENCIA in receitaBrutaCalculada) {
        tipoMetodo = EMetodoReceitaBrutaCalculada.VALOR_REFERENCIA;
      }

      context?.setIdReceitaBrutaCalculadaNCM(receitaBrutaCalculada.id ?? -1);
      context?.setReceitaBrutaCalculadaMetodo(tipoMetodo);
    } else if (type === ETipoOperacao.VENDA_TERRITORIO_NACIONAL_EMPRESAS_COLIGADAS_OU_MESMO_GRUPO_ECONOMICO) {
      context?.setIdVendaNacionalNCM(ncm?.id ?? -1);
    } else if (type === ETipoOperacao.RECEITA_CALCULADA_EXPORTACOES) {
      const ncmExportacoes = ncm as IReceitaExportacoesNCM | IReceitaExportacoesVinculadaNCM;
      context?.setIdReceitaExportacoesNCM(ncmExportacoes?.id ?? -1);
      context?.setHasOperacaoVinculadaReceitaExportacoes(ncmExportacoes?.hasExportacao);
    }
    if (setIsEdicaoNcm) setIsEdicaoNcm(true);
  };

  useEffect(() => {
    if (type === ETipoOperacao.VENDA_TERRITORIO_NACIONAL_EMPRESAS_COLIGADAS_OU_MESMO_GRUPO_ECONOMICO) {
      if ('codigoNCM' in ncm) {
        setCodigoNCM(ncm.codigoNCM);
      }
    } else if (type === ETipoOperacao.RECEITA_BRUTA_TRIBUTOS_INCIDENTES) {
      if ('codigoNcm' in ncm) {
        setCodigoNCM(ncm.codigoNcm);
      }
    } else if (type === ETipoOperacao.RECEITA_BRUTA_CALCULADA || type === ETipoOperacao.RECEITA_CALCULADA_EXPORTACOES) {
      if ('ncmBemMineral' in ncm) {
        setCodigoNCM(ncm.ncmBemMineral.codigo);
      }
    }
  }, [ncm]);

  const accordionContent = () => {
    if (type === ETipoOperacao.RECEITA_BRUTA_TRIBUTOS_INCIDENTES) {
      if ('valorReceitaBruta' in ncm) {
        return <ItemReceitaBruta ncm={ncm} />;
      }
    } else if (type === ETipoOperacao.VENDA_TERRITORIO_NACIONAL_EMPRESAS_COLIGADAS_OU_MESMO_GRUPO_ECONOMICO) {
      return <ItemVendaNacional ncm={ncm as IVendaNacionalNCMReceber} />;
    } else if (type === ETipoOperacao.RECEITA_BRUTA_CALCULADA) {
      return <ItemReceitaBrutaCalculada itemNCM={ncm as INCMReceitaBrutaCalculada} />;
    } else if (type === ETipoOperacao.RECEITA_CALCULADA_EXPORTACOES) {
      return (
        <ItemReceitaCalculadaExportacoes itemNCM={ncm as IReceitaExportacoesNCM | IReceitaExportacoesVinculadaNCM} />
      );
    }
  };

  return (
    <>
      <div
        className={styles.accordion}
        onClick={() => setIsAccordionOpen(!isAccordionOpen)}
        onKeyDown={() => setIsAccordionOpen(!isAccordionOpen)}
      >
        <Modal title='Excluir NCM selecionado' useScrim modalOpened={isModalOpened}>
          <Modal.Body>Deseja mesmo deletar ao NCM selecionado?</Modal.Body>
          <Modal.Footer justify-content='end'>
            <Button secondary small m={2} onClick={() => setIsModalOpened(false)}>
              Cancelar
            </Button>
            <CustomConfirmer asyncCall={() => deleteNCM()}>Confirmar</CustomConfirmer>
          </Modal.Footer>
        </Modal>
        <div className={styles.leftContainer}>
          <span className={styles.iconArrow}>
            {!isAccordionOpen ? (
              <i className={`fas fa-chevron-down fa-sm`} />
            ) : (
              <i className={`fas fa-chevron-up fa-sm`} />
            )}
          </span>
          <div className={styles.title}>NCM - {codigoNCM}</div>
        </div>
        <div className={styles.rightContainer}>
          {!isReadOnly && (
            <>
              <BrButton
                data-testid='btn-editar'
                circle
                size='small'
                icon='fas fa-edit fa-sm'
                onClick={() => {
                  editarNCM();
                }}
              />
              <BrButton
                data-testid='btn-excluir'
                circle
                size='small'
                icon='fas fa-trash fa-sm'
                onClick={() => {
                  setIsModalOpened(true);
                }}
              />
            </>
          )}
        </div>
      </div>
      {isAccordionOpen && <div className={styles.accordionContent}>{accordionContent()}</div>}
    </>
  );
};

export default ListaNCMItem;
